// ==========================================
// FILE: \src\screens\ProfileScreen.js
// ==========================================

import React, { useState, useEffect } from 'react';
import { 
  View, Text, StyleSheet, TouchableOpacity, TextInput, 
  ActivityIndicator, KeyboardAvoidingView, Platform, ScrollView, Modal, Linking
} from 'react-native';
import { 
  updatePassword, signOut, deleteUser, EmailAuthProvider, reauthenticateWithCredential,
  GoogleAuthProvider, OAuthProvider, getAuth
} from 'firebase/auth';
import { 
  doc, getDoc, setDoc, collection, getDocs, deleteDoc 
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import EnergyHeader from '../components/EnergyHeader'; 
import { 
  ArrowLeft, User, Lock, CheckCircle, AlertCircle, 
  Globe, Trash2, HeartCrack, FileText, ChevronRight, ShieldAlert, Crown, Fingerprint, Info
} from 'lucide-react-native';

// IMPORTAZIONE MODULI DI AUTENTICAZIONE SOCIAL PER RE-AUTH NATIVA
import { GoogleSignin } from '@react-native-google-signin/google-signin';

// IMPORTIAMO L'HOOK DELLA PRIVACY PER IL DIRITTO DI REVOCA (GDPR)
import { useConsent } from '../components/CookieConsentBanner';

// IMPORTIAMO L'HOOK CORRETTO DEL CONTESTO
import { useWineContext } from '../context/WineContext';

// URL BASE AGGIORNATO AL DOMINIO UFFICIALE DELLA LANDING PER PREVENIRE REWRITE SPA IN-APP
const BASE_WEB_URL = 'https://vinologic.it';

export default function ProfileScreen({ navigation }) {
  const user = auth.currentUser;
  
  // Estraiamo la funzione per riaprire il banner dal Context
  const { openPreferences } = useConsent(); 

  // RECUPERIAMO IL PROFILO GLOBALE IN MODO SICURO E LE UTILITY DI TRADUZIONE
  const { userProfile, t, changeLanguage } = useWineContext();
  const isPremium = userProfile?.isPremium || false;
  const premiumType = userProfile?.premiumType || 'none';

  // --- STATI PROFILO ---
  const [profileData, setProfileData] = useState({ name: '', country: 'Italia', language: 'it' });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // --- STATI PASSWORD ---
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // --- STATI ELIMINAZIONE GDPR ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isBiometricSupported, setIsBiometricSupported] = useState(false);

  // --- STATI SISTEMA ---
  const [customAlert, setCustomAlert] = useState({ visible: false, title: '', message: '', type: 'info', onConfirm: null });

  // Rilevamento dinamico del canale di autenticazione e lingua corrente
  const providerId = user?.providerData[0]?.providerId || 'password';
  const currentLang = profileData.language || userProfile?.language || 'it';

  // CARICAMENTO INIZIALE PROFILO E VERIFICA HARDWARE BIOMETRICO
  useEffect(() => {
    (async () => {
      const compatible = await LocalAuthentication.hasHardwareAsync();
      const enrolled = await LocalAuthentication.isEnrolledAsync();
      setIsBiometricSupported(compatible && enrolled);
    })();

    const fetchProfile = async () => {
      if (!user) return;
      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setProfileData(prev => ({ ...prev, ...docSnap.data() }));
        }
      } catch (error) {
        console.error("Errore caricamento profilo", error);
      } finally { 
        setIsLoadingProfile(false); 
      }
    };
    fetchProfile();
  }, [user]);

  // FUNZIONI PER ALERT DI SISTEMA
  const showAppAlert = (title, message, type = 'info', onConfirm = null) => {
    setCustomAlert({ visible: true, title, message, type, onConfirm });
  };

  const closeAppAlert = () => {
    setCustomAlert(prev => ({ ...prev, visible: false }));
  };

  // Funzione apertura documenti legali con localizzazione dinamica e URL nativi per lingua
  const openLegalDocument = (type) => {
    const url = type === 'privacy' 
      ? `${BASE_WEB_URL}/${currentLang}/privacy.html` 
      : `${BASE_WEB_URL}/${currentLang}/terms.html`;

    Linking.openURL(url).catch(() => 
      showAppAlert(t('profile.alerts.linkErrorTitle'), t('profile.alerts.linkErrorMsg'), "error")
    );
  };

  // SALVATAGGIO PROFILO
  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const docRef = doc(db, 'users', user.uid);
      await setDoc(docRef, { 
        name: profileData.name.trim(), 
        country: profileData.country, 
        language: profileData.language 
      }, { merge: true });
      
      // Sincronizziamo istantaneamente lo stato globale della lingua nell'applicazione
      await changeLanguage(profileData.language);
      
      showAppAlert(t('profile.alerts.saveSuccessTitle'), t('profile.alerts.saveSuccessMsg'), 'success');
    } catch (error) { 
      showAppAlert(t('profile.alerts.saveErrorTitle'), t('profile.alerts.saveErrorMsg'), 'error'); 
    } finally { 
      setIsSavingProfile(false); 
    }
  };

  // CAMBIO PASSWORD
  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      return showAppAlert(t('profile.alerts.emptyFieldsTitle'), t('profile.alerts.emptyFieldsMsg'), 'error');
    }
    if (newPassword.length < 6) {
      return showAppAlert(t('profile.alerts.shortPasswordTitle'), t('profile.alerts.shortPasswordMsg'), 'error');
    }
    if (newPassword !== confirmPassword) {
      return showAppAlert(t('profile.alerts.mismatchPasswordTitle'), t('profile.alerts.mismatchPasswordMsg'), 'error');
    }
    
    setIsUpdatingPassword(true);
    try {
      await updatePassword(user, newPassword);
      showAppAlert(t('profile.alerts.passwordSuccessTitle'), t('profile.alerts.passwordSuccessMsg'), 'success');
      setNewPassword(''); 
      setConfirmPassword('');
    } catch (error) { 
      showAppAlert(t('profile.alerts.passwordErrorTitle'), t('profile.alerts.passwordErrorMsg'), 'error'); 
    } finally { 
      setIsUpdatingPassword(false); 
    }
  };

  // BIOMETRIA PER ELIMINAZIONE ACCOUNT DI SICUREZZA (MOBILE NATIVO)
  const handleBiometricDelete = async () => {
    try {
      const biometricAuth = await LocalAuthentication.authenticateAsync({
        promptMessage: "Conferma la tua identità per eliminare la cantina",
        disableDeviceFallback: false,
      });

      if (biometricAuth.success) {
        let savedAccounts = [];
        const savedStr = await SecureStore.getItemAsync('savedAccounts');
        if (savedStr) savedAccounts = JSON.parse(savedStr);

        const currentAccount = savedAccounts.find(a => a.email.toLowerCase() === user.email.toLowerCase());
        if (currentAccount?.password) {
          setDeletePassword(currentAccount.password);
          executeDeleteAccount(currentAccount.password);
        } else {
          showAppAlert("Operazione Richiesta", "Inserisci manualmente la password di sicurezza per procedere.", "info");
        }
      }
    } catch (error) {
      showAppAlert("Errore Biometria", "Impossibile autenticare con biometria.", "error");
    }
  };

  // ELIMINAZIONE ACCOUNT COMPLETA E IRREVERSIBILE CON PASSWORD VINOVAULT FORZATA PER EMULARE 2FA SU WEB E SMARTPHONE
  const executeDeleteAccount = async (overridePassword = null) => {
    const passwordToUse = overridePassword || deletePassword;

    if (!passwordToUse) {
      const errorMsg = providerId === 'google.com'
        ? "Inserisci la password di sicurezza VinoLogic creata in fase di configurazione iniziale."
        : "Inserisci la password del tuo account per confermare l'eliminazione.";
      return showAppAlert(t('profile.alerts.deleteWarningTitle'), errorMsg, 'error');
    }
    
    setIsDeletingAccount(true);
    try {
      const activeAuth = Platform.OS === 'web' ? getAuth() : auth;
      
      // Utilizziamo la password VinoLogic collegata all'account (anche se social) per la Re-autenticazione sicura 2FA
      const credential = EmailAuthProvider.credential(user.email, passwordToUse);
      await reauthenticateWithCredential(activeAuth.currentUser, credential);

      console.log("[VinoLogic GDPR] Autenticazione 2FA superata con successo. Rimozione permessi OAuth e sessioni...");

      // Se l'account ha sessioni social attive residue, procediamo alla revoca dei token per bloccare il login automatico browser
      if (providerId === 'google.com') {
        if (Platform.OS === 'web') {
          try {
            const tokenToRevoke = activeAuth.currentUser?.stsTokenManager?.accessToken;
            if (tokenToRevoke) {
              await fetch(`https://oauth2.googleapis.com/revoke?token=${tokenToRevoke}`, { 
                method: 'POST', 
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' } 
              });
            }
          } catch (e) { console.warn("[VinoLogic] Errore revoca token web silenziato:", e); }
        } else {
          try {
            await GoogleSignin.hasPlayServices();
            await GoogleSignin.revokeAccess();
            await GoogleSignin.signOut();
          } catch (e) { console.warn("[VinoLogic] Errore SDK mobile silenziato:", e); }
        }
      }

      // Svuotamento ricorsivo a cascata di tutte le sotto-collezioni dell'utente su Firestore
      const subCollections = ['wines', 'locations', 'terroirs', 'stats'];
      for (const subColl of subCollections) {
        const collRef = collection(db, 'users', user.uid, subColl);
        const snapshot = await getDocs(collRef);
        if (!snapshot.empty) {
          await Promise.all(snapshot.docs.map(docSnap => deleteDoc(docSnap.ref)));
          console.log(`[VinoLogic GDPR] Sotto-collezione "${subColl}" svuotata.`);
        }
      }
      
      // Eliminazione del documento anagrafico principale dell'utente da Firestore
      await deleteDoc(doc(db, 'users', user.uid));
      console.log("[VinoLogic GDPR] Profilo utente rimosso da Firestore.");
      
      // Eliminazione finale ed atomica dell'istanza utente dai server di Auth
      await deleteUser(activeAuth.currentUser || user);
      console.log("[VinoLogic GDPR] Utente rimosso definitivamente da Firebase Auth.");
      
      setShowDeleteModal(false);
      setDeletePassword('');
      
    } catch (error) {
      console.error("Errore durante la rimozione account GDPR:", error);
      setIsDeletingAccount(false);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        const errorWrongMsg = providerId === 'google.com'
          ? "La password di sicurezza inserita non è corretta. Se l'hai dimenticata, effettua un logout e usa il comando 'Password dimenticata' inserendo la tua mail Gmail."
          : "La password inserita non corrisponde. Riprova o usa la procedura di recupero password.";
        showAppAlert("Password Errata", errorWrongMsg, 'error');
      } else {
        showAppAlert(t('profile.alerts.deleteErrorTitle'), error.message || t('profile.alerts.deleteErrorMsg'), 'error');
      }
    }
  };

  return (
    <View style={styles.mainWrapper}>
      
      {/* --- 1. BANNER ROSSO EDGE-TO-EDGE --- */}
      <View style={styles.topRedBanner}>
         <EnergyHeader />
      </View>

      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        
        {/* --- 2. CUSCINO DI NAVIGAZIONE BIANCO --- */}
        <View style={styles.pageHeaderCushion}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <ArrowLeft color="#0f172a" size={24} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('profile.title')}</Text>
          <View style={{width: 40}} /> 
        </View>

        {isLoadingProfile ? (
          <View style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}>
            <ActivityIndicator size="large" color="#e11d48" />
          </View>
        ) : (
          <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
            
            {/* SCHEDA UTENTE */}
            <View style={styles.profileCard}>
              <View style={styles.avatarCircle}>
                <User color="#e11d48" size={48} />
              </View>
              <Text style={styles.emailText}>{user?.email}</Text>
              <View style={styles.badge}>
                <CheckCircle color="#16a34a" size={16} />
                <Text style={styles.badgeText}>{t('profile.accountVerified')}</Text>
              </View>
            </View>

            {/* --- BLOCCO UPGRADE (Visibile solo se abbonato ma non Lifetime) --- */}
            {isPremium && premiumType !== 'lifetime' && (
              <View style={styles.upgradeCard}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                  <Crown color="#facc15" size={24} />
                  <Text style={styles.upgradeTitle}>
                    {premiumType === 'annual' ? t('profile.upgrade.planAnnual') : t('profile.upgrade.planMonthly')}
                  </Text>
                </View>
                <Text style={styles.upgradeDesc}>{t('profile.upgrade.desc')}</Text>
                <TouchableOpacity style={styles.upgradeButton} onPress={() => navigation.navigate('Paywall')}>
                  <Text style={styles.upgradeButtonText}>{t('profile.upgrade.button')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* DATI PERSONALI */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <User color="#0f172a" size={22} />
                <Text style={styles.sectionTitle}>{t('profile.sections.personalData')}</Text>
              </View>
              
              <Text style={styles.label}>{t('profile.fields.nameLabel')}</Text>
              <TextInput 
                style={styles.input} 
                placeholder={t('profile.fields.namePlaceholder')} 
                placeholderTextColor="#94a3b8" 
                value={profileData.name} 
                onChangeText={(text) => setProfileData({...profileData, name: text})} 
              />
              
              <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 8, marginLeft: 4}}>
                <Globe color="#475569" size={16} style={{marginRight: 6}} />
                <Text style={[styles.label, {marginBottom: 0, marginLeft: 0}]}>{t('profile.fields.langCountryLabel')}</Text>
              </View>
              <View style={styles.languageButtons}>
                <TouchableOpacity style={[styles.langBtn, profileData.language === 'it' && styles.langBtnActive]} onPress={() => setProfileData({...profileData, language: 'it', country: 'Italia'})}>
                  <Text style={[styles.langText, profileData.language === 'it' && styles.langTextActive]}>🇮🇹 Italiano</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.langBtn, profileData.language === 'en' && styles.langBtnActive]} onPress={() => setProfileData({...profileData, language: 'en', country: 'International'})}>
                  <Text style={[styles.langText, profileData.language === 'en' && styles.langTextActive]}>🇬🇧 English</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.langBtn, profileData.language === 'fr' && styles.langBtnActive]} onPress={() => setProfileData({...profileData, language: 'fr', country: 'France'})}>
                  <Text style={[styles.langText, profileData.language === 'fr' && styles.langTextActive]}>🇫🇷 Français</Text>
                </TouchableOpacity>
              </View>

              <TouchableOpacity style={styles.updateButton} onPress={handleSaveProfile} disabled={isSavingProfile}>
                {isSavingProfile ? <ActivityIndicator color="#fff" /> : <Text style={styles.updateButtonText}>{t('profile.buttons.saveProfile')}</Text>}
              </TouchableOpacity>
            </View>

            {/* SEZIONE GESTIONE PASSWORD DI SICUREZZA VINOVAULT ESTESA A TUTTI PER EMULARE UNA 2FA IN CANCELLAZIONE */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Lock color="#0f172a" size={22} />
                <Text style={styles.sectionTitle}>
                  {providerId === 'google.com' ? "Aggiorna Password Sicurezza VinoLogic" : "Aggiorna Password Account"}
                </Text>
              </View>
              
              <Text style={styles.label}>{t('profile.fields.passwordLabel')}</Text>
              <TextInput 
                style={styles.input} 
                placeholder={t('profile.fields.passwordPlaceholder')} 
                placeholderTextColor="#94a3b8" 
                value={newPassword} 
                onChangeText={setNewPassword} 
                secureTextEntry 
              />
              
              <Text style={styles.label}>{t('profile.fields.confirmPasswordLabel')}</Text>
              <TextInput 
                style={styles.input} 
                placeholder={t('profile.fields.confirmPasswordPlaceholder')} 
                placeholderTextColor="#94a3b8" 
                value={confirmPassword} 
                onChangeText={setConfirmPassword} 
                secureTextEntry 
              />
              
              <TouchableOpacity 
                style={[styles.updateButton, { backgroundColor: '#475569' }]} 
                onPress={handleUpdatePassword} 
                disabled={isUpdatingPassword}
              >
                {isUpdatingPassword ? <ActivityIndicator color="#fff" /> : <Text style={styles.updateButtonText}>{t('profile.buttons.changePassword')}</Text>}
              </TouchableOpacity>
            </View>

            {/* SEZIONE INFORMAZIONI LEGALI */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <FileText color="#0f172a" size={22} />
                <Text style={styles.sectionTitle}>{t('profile.sections.legal')}</Text>
              </View>
              
              <TouchableOpacity style={styles.legalBtn} onPress={() => openLegalDocument('terms')}>
                <Text style={styles.legalBtnText}>{t('profile.buttons.terms')}</Text>
                <ChevronRight color="#94a3b8" size={20} />
              </TouchableOpacity>
              
              <TouchableOpacity style={[styles.legalBtn, { marginTop: 12 }]} onPress={() => openLegalDocument('privacy')}>
                <Text style={styles.legalBtnText}>{t('profile.buttons.privacy')}</Text>
                <ChevronRight color="#94a3b8" size={20} />
              </TouchableOpacity>

              {/* GESTIONE PREFERENZE COOKIE E TRACCIAMENTO */}
              <TouchableOpacity style={[styles.legalBtn, { borderBottomWidth: 0, paddingBottom: 0, marginTop: 12 }]} onPress={openPreferences}>
                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                  <ShieldAlert color="#3b82f6" size={18} style={{marginRight: 8}}/>
                  <Text style={[styles.legalBtnText, {color: '#3b82f6', fontWeight: 'bold'}]}>{t('profile.buttons.cookies')}</Text>
                </View>
                <ChevronRight color="#3b82f6" size={20} />
              </TouchableOpacity>
            </View>

            {/* DANGER ZONE (ELIMINAZIONE ACCOUNT CONFORME GDPR) */}
            <View style={styles.dangerZone}>
              <TouchableOpacity style={styles.deleteAccountButton} onPress={() => setShowDeleteModal(true)}>
                <Trash2 color="#e11d48" size={20} />
                <Text style={styles.deleteAccountText}>{t('profile.buttons.deleteAccount')}</Text>
              </TouchableOpacity>
              <Text style={styles.dangerText}>{t('profile.dangerZoneDesc')}</Text>
            </View>

          </ScrollView>
        )}

        {/* MODALE DI CONFERMA ELIMINAZIONE AD ALTA PROTEZIONE CON PASS-BOX UNIFICATO */}
        <Modal transparent={true} visible={showDeleteModal} animationType="slide">
          <View style={styles.modalOverlay}>
            <View style={styles.modalBox}>
              <View style={styles.modalHeader}>
                <HeartCrack color="#e11d48" size={28} />
                <Text style={styles.modalTitle}>{t('profile.deleteModal.title')}</Text>
              </View>
              
              <Text style={styles.modalMessage}>
                {providerId === 'google.com'
                  ? "Inserisci la password di sicurezza VinoLogic creata durante la configurazione iniziale per confermare l'eliminazione totale, atomica e irreversibile (GDPR) di tutti i dati della tua cantina virtuale. Se l'hai dimenticata, effettua un logout e usa il comando 'Password dimenticata' per reimpostarla."
                  : "Inserisci la tua password di accesso VinoLogic per confermare l'eliminazione totale, atomica e irreversibile (GDPR) di tutti i dati della tua cantina virtuale."
                }
              </Text>
              
              <TextInput 
                style={styles.deleteInput} 
                placeholder={providerId === 'google.com' ? "Password di sicurezza VinoLogic" : "Password dell'account"} 
                placeholderTextColor="#94a3b8" 
                value={deletePassword} 
                onChangeText={setDeletePassword} 
                secureTextEntry 
              />
              
              <View style={styles.modalButtonsColumn}>
                <TouchableOpacity 
                  style={styles.stayButton} 
                  onPress={() => { setShowDeleteModal(false); setDeletePassword(''); }} 
                  disabled={isDeletingAccount}
                >
                  <Text style={styles.stayButtonText}>{t('profile.deleteModal.stayButton')}</Text>
                </TouchableOpacity>
                
                <View style={{ flexDirection: 'row', gap: 10, width: '100%' }}>
                  {Platform.OS !== 'web' && isBiometricSupported && (
                    <TouchableOpacity 
                      style={[styles.confirmDeleteButton, { flex: 0.25, backgroundColor: '#f1f5f9', borderColor: '#cbd5e1' }]} 
                      onPress={handleBiometricDelete}
                      disabled={isDeletingAccount}
                    >
                      <Fingerprint color="#475569" size={24} />
                    </TouchableOpacity>
                  )}
                  
                  <TouchableOpacity 
                    style={[
                      styles.confirmDeleteButton, 
                      { flex: Platform.OS !== 'web' && isBiometricSupported ? 0.75 : 1 },
                      !deletePassword && { opacity: 0.4, backgroundColor: '#cbd5e1', borderColor: '#cbd5e1' }
                    ]} 
                    onPress={() => executeDeleteAccount()} 
                    disabled={isDeletingAccount || !deletePassword}
                  >
                    {isDeletingAccount ? (
                      <ActivityIndicator color="#e11d48" />
                    ) : (
                      <Text style={[styles.confirmDeleteText, !deletePassword && { color: '#94a3b8' }]}>
                        Conferma ed Elimina
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>
        </Modal>

        {/* MODALE AVVISI DI SISTEMA (SUCCESS/ERROR) */}
        <Modal transparent={true} visible={customAlert.visible} animationType="fade">
          <View style={styles.modalOverlay}>
            <View style={styles.modalBox}>
              <View style={styles.modalHeader}>
                {customAlert.type === 'error' ? (
                  <AlertCircle color="#ef4444" size={28} />
                ) : customAlert.type === 'success' ? (
                  <CheckCircle color="#10b981" size={28} />
                ) : (
                  <Info color="#3b82f6" size={28} />
                )}
                <Text style={styles.modalTitle}>{customAlert.title}</Text>
              </View>
              <Text style={styles.modalMessage}>{customAlert.message}</Text>
              <View style={styles.modalButtons}>
                <TouchableOpacity style={[styles.modalConfirmBtn, customAlert.type === 'error' ? { backgroundColor: '#e11d48' } : customAlert.type === 'success' ? { backgroundColor: '#10b981' } : { backgroundColor: '#0f172a' }]} onPress={closeAppAlert}>
                  <Text style={styles.modalConfirmText}>{t('login.buttons.dismiss')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  mainWrapper: { 
    flex: 1, 
    backgroundColor: '#f8fafc' 
  },
  container: { 
    flex: 1 
  },
  topRedBanner: {
    backgroundColor: '#e11d48',
    paddingBottom: 16,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    elevation: 5,
    zIndex: 50,
    ...Platform.select({ web: { boxShadow: '0px 4px 6px rgba(225, 29, 72, 0.3)' } })
  },
  pageHeaderCushion: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16, 
    marginBottom: 16,
    padding: 16,
    borderRadius: 20,
    elevation: 3,
    zIndex: 10,
    ...Platform.select({ web: { boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)' } })
  },
  backButton: { 
    padding: 8, 
    backgroundColor: '#f1f5f9', 
    borderRadius: 12 
  },
  headerTitle: { 
    fontSize: 20, 
    fontWeight: 'bold', 
    color: '#0f172a' 
  },
  content: { 
    paddingHorizontal: 16, 
    pt: 8,
    paddingTop: 8,
    paddingBottom: 60 
  },
  profileCard: { 
    alignItems: 'center', 
    backgroundColor: 'white', 
    padding: 32, 
    borderRadius: 24, 
    marginBottom: 24, 
    elevation: 3,
    ...Platform.select({ web: { boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)' } })
  },
  avatarCircle: { 
    width: 80, 
    height: 80, 
    borderRadius: 40, 
    backgroundColor: '#fff1f2', 
    alignItems: 'center', 
    justifyContent: 'center', 
    marginBottom: 16 
  },
  emailText: { 
    fontSize: 18, 
    fontWeight: 'bold', 
    color: '#0f172a', 
    marginBottom: 8 
  },
  badge: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    backgroundColor: '#dcfce7', 
    paddingHorizontal: 12, 
    paddingVertical: 6, 
    borderRadius: 20 
  },
  badgeText: { 
    color: '#166534', 
    fontWeight: 'bold', 
    fontSize: 12, 
    marginLeft: 6 
  },
  upgradeCard: {
    backgroundColor: '#0f172a', padding: 24, borderRadius: 24, marginBottom: 24, 
    borderWidth: 1, borderColor: '#334155', elevation: 4,
    ...Platform.select({ web: { boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)' } })
  },
  upgradeTitle: { color: '#f8fafc', fontSize: 18, fontWeight: 'bold', marginLeft: 10 },
  upgradeDesc: { color: '#cbd5e1', fontSize: 14, marginBottom: 20, lineHeight: 22 },
  upgradeButton: { backgroundColor: '#e11d48', paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  upgradeButtonText: { color: 'white', fontWeight: 'bold', fontSize: 16 },
  section: { 
    backgroundColor: 'white', 
    padding: 24, 
    borderRadius: 24, 
    marginBottom: 24, 
    elevation: 2,
    ...Platform.select({ web: { boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)' } })
  },
  sectionHeader: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    marginBottom: 20 
  },
  sectionTitle: { 
    fontSize: 18, 
    fontWeight: 'bold', 
    color: '#0f172a', 
    marginLeft: 10 
  },
  label: { 
    fontSize: 14, 
    fontWeight: '600', 
    color: '#475569', 
    marginBottom: 8, 
    marginLeft: 4 
  },
  input: { 
    backgroundColor: '#f8fafc', 
    borderWidth: 1, 
    borderColor: '#e2e8f0', 
    borderRadius: 12, 
    padding: 14, 
    fontSize: 16, 
    color: '#0f172a', 
    marginBottom: 20 
  },
  languageButtons: { 
    flexDirection: 'row', 
    flexWrap: 'wrap',
    gap: 8, 
    marginBottom: 24 
  },
  langBtn: { 
    flex: 1, 
    minWidth: 95,
    paddingVertical: 12, 
    borderRadius: 12, 
    borderWidth: 1, 
    borderColor: '#e2e8f0', 
    alignItems: 'center', 
    backgroundColor: '#f8fafc' 
  },
  langBtnActive: { 
    backgroundColor: '#0f172a', 
    borderColor: '#0f172a' 
  },
  langText: { 
    color: '#64748b', 
    fontWeight: '600',
    fontSize: 13
  },
  langTextActive: { 
    color: 'white' 
  },
  updateButton: { 
    backgroundColor: '#0f172a', 
    padding: 16, 
    borderRadius: 12, 
    alignItems: 'center', 
    marginTop: 8 
  },
  updateButtonText: { 
    color: 'white', 
    fontWeight: 'bold', 
    fontSize: 16 
  },
  legalBtn: { 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    paddingVertical: 14, 
    borderBottomWidth: 1, 
    borderBottomColor: '#f1f5f9' 
  },
  legalBtnText: { 
    fontSize: 15, 
    color: '#475569', 
    fontWeight: '500' 
  },
  dangerZone: { 
    marginTop: 10, 
    marginBottom: 40, 
    alignItems: 'center' 
  },
  deleteAccountButton: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    justifyContent: 'center', 
    backgroundColor: 'transparent', 
    padding: 16, 
    borderRadius: 16, 
    borderWidth: 1, 
    borderColor: '#e11d48', 
    width: '100%', 
    borderStyle: 'dashed' 
  },
  deleteAccountText: { 
    color: '#e11d48', 
    fontWeight: 'bold', 
    fontSize: 16, 
    marginLeft: 8 
  },
  dangerText: { 
    color: '#94a3b8', 
    fontSize: 12, 
    textAlign: 'center', 
    marginTop: 12, 
    paddingHorizontal: 10,
    lineHeight: 18
  },
  deleteInput: { 
    backgroundColor: '#f8fafc', 
    borderWidth: 1, 
    borderColor: '#e2e8f0', 
    borderRadius: 12, 
    padding: 14, 
    fontSize: 16, 
    color: '#0f172a', 
    marginBottom: 24 
  },
  modalButtonsColumn: { 
    flexDirection: 'column', 
    gap: 12 
  },
  stayButton: { 
    backgroundColor: '#0f172a', 
    paddingVertical: 14, 
    borderRadius: 12, 
    alignItems: 'center' 
  },
  stayButtonText: { 
    color: 'white', 
    fontWeight: 'bold', 
    fontSize: 16 
  },
  confirmDeleteButton: { 
    backgroundColor: '#fff1f2', 
    paddingVertical: 14, 
    borderRadius: 12, 
    alignItems: 'center', 
    borderWidth: 1, 
    borderColor: '#ffe4e6' 
  },
  confirmDeleteText: { 
    color: '#be123c', 
    fontWeight: 'bold', 
    fontSize: 16 
  },
  modalOverlay: { 
    flex: 1, 
    backgroundColor: 'rgba(15, 23, 42, 0.6)', 
    justifyContent: 'center', 
    alignItems: 'center', 
    padding: 20 
  },
  modalBox: { 
    backgroundColor: 'white', 
    borderRadius: 24, 
    padding: 24, 
    width: '100%', 
    maxWidth: 400, 
    elevation: 10,
    ...Platform.select({ web: { boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.2)' } })
  },
  modalHeader: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    marginBottom: 16 
  },
  modalTitle: { 
    fontSize: 20, 
    fontWeight: 'bold', 
    color: '#0f172a', 
    marginLeft: 12, 
    flex: 1 
  },
  modalMessage: { 
    fontSize: 16, 
    color: '#475569', 
    lineHeight: 24, 
    marginBottom: 24 
  },
  modalButtons: { 
    flexDirection: 'row', 
    justifyContent: 'flex-end', 
    gap: 12 
  },
  modalConfirmBtn: { 
    paddingVertical: 12, 
    paddingHorizontal: 20, 
    borderRadius: 12, 
    minWidth: 100, 
    alignItems: 'center' 
  },
  modalConfirmText: { 
    color: 'white', 
    fontWeight: 'bold', 
    fontSize: 16 
  }
});